/**
 * jaiio-product-gallery.js — galerie de la fiche produit (`<jaiio-gallery>`).
 *
 * Balisage : `snippets/product-gallery.liquid` ; mise en page :
 * `assets/jaiio-product.css`. Ce fichier ne fait que trois choses :
 * 1. savoir quelle photo est « la courante » — en desktop celle qui occupe le
 *    plus l'écran dans le bento, en mobile celle posée dans la piste — et le
 *    dire aux vignettes et aux compteurs ;
 * 2. aller à une photo depuis une vignette (défilement de page en desktop,
 *    de piste en mobile) ;
 * 3. la visionneuse, un `<dialog>` modal : flèches, vignettes, clic = zoom
 *    qui suit le pointeur (×2,5 à la souris, ×2 au doigt), glissement pour
 *    changer de photo.
 * S'y ajoute, en bas de fichier, le `top` de la colonne info collée (T038) :
 * même besoin de connaître la hauteur de l'en-tête collant.
 *
 * Remplace `<product-media>` de Focal sur la fiche : plus de Flickity, plus de
 * PhotoSwipe. La photo de la variante choisie reste suivie (`variant:changed`).
 */
(function () {
  'use strict';

  var DESKTOP = window.matchMedia('(min-width: 1000px)');
  var COARSE = window.matchMedia('(pointer: coarse)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var SWIPE = 40; // px : en deçà, un geste est un toucher, pas un glissement

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  // Le chargement d'une photo préchargée ne doit pas amorcer celui de toute la
  // galerie. Seule l'image réellement affichée appelle cette suite.
  function afterImageLoaded(image, callback) {
    var active = true;
    function cleanup() {
      active = false;
      image.removeEventListener('load', loaded);
      image.removeEventListener('error', cleanup);
    }
    function loaded() {
      if (!active) return;
      cleanup();
      if (image.naturalWidth > 0) callback();
    }
    image.addEventListener('load', loaded);
    image.addEventListener('error', cleanup);
    if (image.complete && image.naturalWidth > 0) loaded();
    return cleanup;
  }

  // Hauteur de l'en-tête s'il colle en haut de page, pour qu'un saut ne glisse
  // pas la photo dessous. Même formule que Focal (`theme.js`, en-tête collant).
  function stickyOffset() {
    var style = getComputedStyle(document.documentElement);
    return (parseInt(style.getPropertyValue('--header-height'), 10) || 0) *
      (parseInt(style.getPropertyValue('--enable-sticky-header'), 10) || 0);
  }

  class JaiioGallery extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      this.track = this.querySelector('[data-gallery-track]');
      this.counters = Array.from(this.querySelectorAll('[data-gallery-count]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.current = 0;
      this.ratios = this.items.map(function () { return 0; });
      this._preloads = new Map();

      this.addEventListener('click', this._onClick.bind(this));

      if (this.items.length > 1) {
        this._observe();
        this.track.addEventListener('scroll', this._onTrackScroll.bind(this), { passive: true });
        DESKTOP.addEventListener('change', this._sync.bind(this));
      }

      var form = document.getElementById(this.dataset.formId);
      if (form) form.addEventListener('variant:changed', this._onVariantChanged.bind(this));

      this._setupViewer();
      this._watchPageImage(this.current);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this._pageImageCleanup) this._pageImageCleanup();
      if (this._viewerImageCleanup) this._viewerImageCleanup();
    }

    /* ── Photo courante ─────────────────────────────────────────────── */

    // Desktop : deux photos se partagent souvent l'écran (bento) ; la courante
    // est celle qui en montre le plus, la première à égalité.
    _observe() {
      if (!('IntersectionObserver' in window)) return;
      var self = this;
      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          self.ratios[Number(entry.target.dataset.galleryItem)] = entry.isIntersecting ? entry.intersectionRatio : 0;
        });
        if (!DESKTOP.matches) return;
        var best = 0;
        self.ratios.forEach(function (r, i) { if (r > self.ratios[best]) best = i; });
        if (self.ratios[best] > 0) self._setCurrent(best);
      }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
      this.items.forEach(function (item) { self.observer.observe(item); });
    }

    _onTrackScroll() {
      if (DESKTOP.matches || this._scrollFrame) return;
      var self = this;
      this._scrollFrame = requestAnimationFrame(function () {
        self._scrollFrame = null;
        var width = self.track.clientWidth || 1;
        self._setCurrent(Math.round(Math.abs(self.track.scrollLeft) / width));
      });
    }

    // Changement de largeur : la courante se relit dans le mode qui s'ouvre.
    _sync() {
      if (DESKTOP.matches) {
        var best = 0, self = this;
        this.ratios.forEach(function (r, i) { if (r > self.ratios[best]) best = i; });
        this._setCurrent(best);
      } else {
        this._onTrackScroll();
      }
    }

    _setCurrent(index, watchPageImage) {
      index = Math.max(0, Math.min(this.items.length - 1, index));
      this.current = index;
      var label = pad(index + 1) + ' / ' + pad(this.items.length);
      this.counters.forEach(function (el) { el.textContent = label; });
      this.thumbs.forEach(function (thumb) {
        if (Number(thumb.dataset.galleryThumb) === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (watchPageImage !== false) this._watchPageImage(index);
    }

    // Les URL/srcset/alt des photos restent dans le HTML serveur. Une seule
    // requête responsive de plus part après la photo courante, à priorité basse.
    // L'image hors DOM réutilise exactement les tailles de son futur affichage.
    _preloadImage(src, srcset, sizes) {
      if (!src && !srcset) return;
      var key = [src, srcset, sizes].join('\n');
      if (this._preloads.has(key)) return;
      var image = new Image(), self = this;
      this._preloads.set(key, image);
      image.decoding = 'async';
      image.fetchPriority = 'low';
      image.onerror = function () { self._preloads.delete(key); };
      if (sizes) image.sizes = sizes;
      if (srcset) image.srcset = srcset;
      if (src) image.src = src;
    }

    _watchPageImage(index) {
      if (this._pageImageCleanup) this._pageImageCleanup();
      var item = this.items[index];
      var image = item && item.dataset.mediaType === 'image' && item.querySelector('img');
      if (!image) return;
      var self = this;
      this._pageImageCleanup = afterImageLoaded(image, function () {
        if (!self.isConnected || self.current !== index || (self.dialog && self.dialog.open)) return;
        var next = self.items.slice(index + 1).find(function (candidate) {
          return candidate.dataset.mediaType === 'image';
        });
        var nextImage = next && next.querySelector('img');
        if (!nextImage || (nextImage.complete && nextImage.naturalWidth > 0)) return;
        self._preloadImage(nextImage.getAttribute('src'), nextImage.getAttribute('srcset'), nextImage.getAttribute('sizes'));
      });
    }

    go(index) {
      var item = this.items[index];
      if (!item) return;
      var behavior = REDUCED.matches ? 'auto' : 'smooth';
      if (DESKTOP.matches) {
        var top = item.getBoundingClientRect().top + window.pageYOffset - stickyOffset() - 20;
        window.scrollTo({ top: Math.max(0, top), behavior: behavior });
      } else {
        this.track.scrollTo({ left: this.track.clientWidth * index, behavior: behavior });
      }
      this._setCurrent(index);
    }

    _onVariantChanged(event) {
      var media = event.detail && event.detail.variant && event.detail.variant.featured_media;
      if (!media) return;
      var item = this.items.find(function (el) { return Number(el.dataset.mediaId) === media.id; });
      if (item) this.go(Number(item.dataset.galleryItem));
    }

    _onClick(event) {
      var thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb && this.contains(thumb)) { this.go(Number(thumb.dataset.galleryThumb)); return; }
      var open = event.target.closest('[data-gallery-open]');
      if (open) this.openViewer(Number(open.dataset.galleryOpen));
    }

    /* ── Visionneuse ────────────────────────────────────────────────── */

    _setupViewer() {
      var dialog = this.dialog = this.querySelector('[data-gallery-dialog]');
      if (!dialog) return;
      this.lb = {
        img: dialog.querySelector('[data-gallery-lb-img]'),
        zoom: dialog.querySelector('[data-gallery-zoom]'),
        count: dialog.querySelector('[data-gallery-lb-count]'),
        hint: dialog.querySelector('[data-gallery-hint]'),
        thumbs: Array.from(dialog.querySelectorAll('[data-gallery-lb-thumb]'))
      };
      // La visionneuse ne montre que des photos : vidéos et 3D restent dans la page.
      this.openers = Array.from(this.querySelectorAll('[data-gallery-open]'));
      this.zoomed = false;
      var self = this;

      dialog.addEventListener('click', function (event) {
        if (event.target.closest('[data-gallery-close]')) { dialog.close(); return; }
        var step = event.target.closest('[data-gallery-step]');
        if (step) { self._step(Number(step.dataset.galleryStep)); return; }
        var thumb = event.target.closest('[data-gallery-lb-thumb]');
        if (thumb) self._show(self._openerIndexOf(Number(thumb.dataset.galleryLbThumb)));
      });
      dialog.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowRight') { event.preventDefault(); self._step(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); self._step(-1); }
      });
      dialog.addEventListener('close', function () {
        document.documentElement.classList.remove('pg-lb-open');
        self._setZoom(false);
        if (self._viewerImageCleanup) self._viewerImageCleanup();
        self._watchPageImage(self.current);
      });

      this._setupZoom();
    }

    // Position d'une photo parmi celles que la visionneuse sait montrer.
    _openerIndexOf(itemIndex) {
      return this.openers.findIndex(function (el) { return Number(el.dataset.galleryOpen) === itemIndex; });
    }

    openViewer(itemIndex) {
      if (!this.dialog) return;
      this._show(Math.max(0, this._openerIndexOf(itemIndex)));
      document.documentElement.classList.add('pg-lb-open');
      this.dialog.showModal();
    }

    _show(position) {
      var n = this.openers.length;
      if (!n) return;
      this.lbIndex = (position + n) % n;
      var opener = this.openers[this.lbIndex];
      var itemIndex = Number(opener.dataset.galleryOpen);
      var source = opener.querySelector('img');
      if (this._viewerImageCleanup) this._viewerImageCleanup();
      if (this._pageImageCleanup) this._pageImageCleanup();
      this._setZoom(false);
      // Grande version chargée à l'ouverture seulement ; `sizes="100vw"` (dans
      // le balisage) laisse le navigateur prendre la taille de l'écran.
      this.lb.img.srcset = opener.dataset.zoomSrcset || '';
      this.lb.img.src = opener.dataset.zoomSrc || (source ? source.src : '');
      this.lb.img.alt = source ? source.alt : '';
      this.lb.img.decoding = 'async';
      var self = this, current = this.lbIndex;
      this._viewerImageCleanup = afterImageLoaded(this.lb.img, function () {
        if (!self.isConnected || self.lbIndex !== current || n < 2) return;
        var next = self.openers[(current + 1) % n];
        var nextSource = next.querySelector('img');
        self._preloadImage(next.dataset.zoomSrc || (nextSource ? nextSource.src : ''),
          next.dataset.zoomSrcset || '', self.lb.img.getAttribute('sizes') || '100vw');
      });
      this.lb.count.textContent = pad(this.lbIndex + 1) + ' / ' + pad(n);
      this.lb.thumbs.forEach(function (thumb) {
        if (Number(thumb.dataset.galleryLbThumb) === itemIndex) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      // La page suit : en refermant, on retrouve la photo qu'on regardait.
      this._setCurrent(itemIndex, false);
    }

    _step(delta) {
      this._show(this.lbIndex + delta);
    }

    _setZoom(on, x, y) {
      this.zoomed = on;
      if (!this.lb) return;
      this.lb.zoom.classList.toggle('is-zoomed', on);
      this.lb.zoom.setAttribute('aria-pressed', String(on));
      if (x != null) this._setOrigin(x, y);
      else if (!on) this.lb.img.style.transformOrigin = '';
      var d = this.dialog.dataset, touch = COARSE.matches;
      this.lb.hint.textContent = on ? (touch ? d.hintUnzoomTouch : d.hintUnzoom) : (touch ? d.hintZoomTouch : d.hintZoom);
    }

    _setOrigin(clientX, clientY) {
      var r = this.lb.zoom.getBoundingClientRect();
      var px = Math.max(0, Math.min(100, (clientX - r.left) / r.width * 100));
      var py = Math.max(0, Math.min(100, (clientY - r.top) / r.height * 100));
      this.lb.img.style.transformOrigin = px.toFixed(1) + '% ' + py.toFixed(1) + '%';
    }

    // Un seul jeu d'événements pointeur pour la souris et le doigt :
    // - souris : clic = zoom au point cliqué, le zoom suit ensuite le pointeur ;
    // - doigt : toucher = zoom, glisser zoomé = explorer, glisser non zoomé =
    //   photo suivante ou précédente.
    // Au clavier, le bouton reçoit un `click` sans coordonnées : zoom au centre.
    _setupZoom() {
      var self = this, zoom = this.lb.zoom, start = null;

      zoom.addEventListener('pointerdown', function (event) {
        start = { x: event.clientX, y: event.clientY, type: event.pointerType };
      });
      zoom.addEventListener('pointermove', function (event) {
        if (!self.zoomed) return;
        if (event.pointerType === 'mouse' || start) self._setOrigin(event.clientX, event.clientY);
      });
      zoom.addEventListener('pointerup', function (event) {
        if (!start) return;
        var dx = event.clientX - start.x, dy = event.clientY - start.y;
        var swipe = start.type !== 'mouse' && !self.zoomed && Math.abs(dx) > SWIPE && Math.abs(dx) > Math.abs(dy);
        self._pointerHandled = true;
        if (swipe) self._step(dx < 0 ? 1 : -1);
        else if (Math.abs(dx) < 10 && Math.abs(dy) < 10) self._setZoom(!self.zoomed, event.clientX, event.clientY);
        start = null;
      });
      zoom.addEventListener('pointercancel', function () { start = null; });
      zoom.addEventListener('click', function () {
        // Le pointeur a déjà décidé ; ce `click` n'est utile qu'au clavier.
        if (self._pointerHandled) { self._pointerHandled = false; return; }
        var r = zoom.getBoundingClientRect();
        self._setZoom(!self.zoomed, r.left + r.width / 2, r.top + r.height / 2);
      });
    }
  }

  if (!customElements.get('jaiio-gallery')) customElements.define('jaiio-gallery', JaiioGallery);

  // Colonne info collée en desktop (T038). Elle est souvent plus haute que
  // l'écran (accordéon « Description » ouvert) : collée par le haut, son bas
  // resterait hors de vue jusqu'au bout de la galerie. Son `top` devient donc
  // négatif quand elle déborde — elle défile avec la page jusqu'à montrer son
  // bas, puis s'arrête. Le CSS lit `--pp-info-top` ; sans script, il colle
  // simplement sous l'en-tête.
  function stickInfo() {
    var info = document.querySelector('.product--gallery > .pp-info');
    if (!info || !('ResizeObserver' in window)) return;
    var GAP = 20;
    var place = function () {
      var top = stickyOffset() + GAP;
      var room = window.innerHeight - info.offsetHeight - GAP;
      info.style.setProperty('--pp-info-top', Math.min(top, room) + 'px');
    };
    new ResizeObserver(place).observe(info);
    window.addEventListener('resize', place);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', stickInfo);
  else stickInfo();
})();
